import {
    AppBar, Avatar, Box, Divider, Drawer, IconButton,
    List, ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography,
} from '@mui/material';
import { IconBuildingCommunity, IconLayoutDashboard, IconLogout, IconMenu2, IconPlus, IconTag } from '@tabler/icons';
import jwt from 'jwtservice/jwtService';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { THEME_COLOR_DARK } from 'utils/Constants';

const SIDEBAR_WIDTH = 260;

const NAV = [
    { label: 'Dashboard',   Icon: IconLayoutDashboard,    path: '/platform/dashboard' },
    { label: 'All Tenants', Icon: IconBuildingCommunity,  path: '/platform/tenants' },
    { label: 'Plans',       Icon: IconTag,                 path: '/platform/plans' },
    { label: 'New Tenant',  Icon: IconPlus,                path: '/platform/create-tenant' },
];

function SidebarContent({ location, navigate, user, initials, handleLogout, onNavClick }) {
    return (
        <Box sx={{
            height: '100%',
            background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
            display: 'flex', flexDirection: 'column',
        }}>
            {/* Brand */}
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <img src="/logo-isp-daftar.png" alt="IspDaftar" style={{ height: '48px', width: 'auto', objectFit: 'contain', borderRadius: '8px' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ color: '#f1f5f9', fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>
                            Isp<Box component="span" sx={{ color: THEME_COLOR_DARK }}>Daftar</Box>
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: 11, fontWeight: 500, letterSpacing: 0.5 }}>
                            Platform Admin
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />

            {/* Nav */}
            <Box sx={{ flex: 1, p: 2 }}>
                <Typography sx={{
                    color: '#475569', fontSize: 10, fontWeight: 700,
                    letterSpacing: 1.2, px: 1, mb: 1.5, textTransform: 'uppercase',
                }}>
                    Management
                </Typography>
                <List disablePadding>
                    {NAV.map(({ label, Icon, path }) => {
                        const active = location.pathname === path;
                        return (
                            <ListItemButton
                                key={path}
                                onClick={() => { navigate(path); onNavClick?.(); }}
                                sx={{
                                    borderRadius: '10px', mb: 0.5, px: 1.5, py: 1,
                                    border: `1px solid ${active ? `${THEME_COLOR_DARK}30` : 'transparent'}`,
                                    bgcolor: active ? `${THEME_COLOR_DARK}18` : 'transparent',
                                    '&:hover': { bgcolor: active ? `${THEME_COLOR_DARK}22` : 'rgba(255,255,255,0.04)' },
                                    transition: 'all 0.15s',
                                }}
                            >
                                <Box sx={{ minWidth: 36, color: active ? THEME_COLOR_DARK : '#64748b' }}>
                                    <Icon size={20} />
                                </Box>
                                <Box
                                    primary={label}
                                    sx={{
                                        fontSize: 13.5, fontWeight: active ? 600 : 400,
                                        color: active ? '#000000' : '#ffffff',
                                    }}
                                />
                                <Typography sx={{color: '#ffffff'}}>{label}</Typography>
                                {/* {active && (
                                    <Box sx={{ width: 3, height: 18, borderRadius: 4, bgcolor: THEME_COLOR_DARK, ml: 1 }} />
                                )} */}
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>

            {/* User footer */}
            <Box sx={{ p: 2 }}>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, px: 0.5 }}>
                    <Avatar sx={{
                        width: 34, height: 34, fontSize: 13, fontWeight: 700,
                        background: `linear-gradient(135deg, ${THEME_COLOR_DARK}, #f59e0b)`,
                    }}>
                        {initials}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {user?.name || 'Platform Admin'}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {user?.email || ''}
                        </Typography>
                    </Box>
                </Box>
                <ListItemButton
                    onClick={handleLogout}
                    sx={{ borderRadius: '8px', px: 1.5, py: 0.75, '&:hover': { bgcolor: 'rgba(239,68,68,0.1)' } }}
                >
                    <ListItemIcon sx={{ minWidth: 32, color: '#ef4444' }}>
                        <IconLogout size={18} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Logout"
                        primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                        sx={{ '& .MuiListItemText-primary': { color: '#fff' } }}
                    />
                </ListItemButton>
            </Box>
        </Box>
    );
}

export default function PlatformLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const isAuth = jwt.getIsLogin() && jwt.isPlatformAdmin();

    const handleLogout = () => {
        jwt.removeToken();
        jwt.removeRefreshtoken();
        jwt.removeUser();
        jwt.removeIsLogin();
        window.location.href = '/platform/login';
    };

    if (!isAuth) return <Outlet />;

    const user = jwt.getUser();
    const initials = user?.name?.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) || 'PA';

    const sidebarProps = { location, navigate, user, initials, handleLogout };

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f1f5f9' }}>

            {/* Desktop sidebar */}
            <Box sx={{
                width: SIDEBAR_WIDTH, flexShrink: 0,
                position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100,
                boxShadow: '4px 0 24px rgba(0,0,0,0.2)',
                display: { xs: 'none', lg: 'flex' }, flexDirection: 'column',
            }}>
                <SidebarContent {...sidebarProps} />
            </Box>

            {/* Mobile drawer */}
            <Drawer
                anchor="left"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                sx={{
                    display: { xs: 'block', lg: 'none' },
                    '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, border: 'none' },
                }}
            >
                <SidebarContent {...sidebarProps} onNavClick={() => setMobileOpen(false)} />
            </Drawer>

            {/* Mobile top AppBar */}
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    display: { xs: 'flex', lg: 'none' },
                    bgcolor: '#0f172a',
                    borderBottom: '1px solid rgba(255,255,255,0.07)',
                    zIndex: 99,
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', minHeight: 56 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 32, height: 32, borderRadius: '9px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <img src="/logo-isp-daftar.png" alt="IspDaftar" style={{ height: '48px', width: 'auto', objectFit: 'contain', borderRadius: '8px' }} />
                        </Box>
                        <Typography sx={{ color: '#f1f5f9', fontWeight: 800, fontSize: 15 }}>
                            Isp<Box component="span" sx={{ color: THEME_COLOR_DARK }}>Daftar</Box>
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setMobileOpen(true)} sx={{ color: '#94a3b8' }}>
                        <IconMenu2 size={22} />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Main content */}
            <Box sx={{
                flex: 1,
                ml: { xs: 0, lg: `${SIDEBAR_WIDTH}px` },
                mt: { xs: '98px', lg: 0 },
                minHeight: '100vh',
                p: { xs: '16px', sm: '20px 24px', lg: '28px 32px' },
            }}>
                <Outlet />
            </Box>
        </Box>
    );
}
